import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageCircle, Phone, Store, Users } from 'lucide-react';
import { produceAPI } from '@/lib/api';
import { HomeSectionTitle } from '@/components/home/HomeBlocks';
import { fmtDate, fmtQty, unitLabel } from '@/components/produce/OfferCard';

/**
 * My cluster (PR 67): for a farmer the support team has made manager of
 * a cluster or association. Each one's total produce on offer (what
 * offtakers see), each farmer's stock with their phone to arrange
 * collection, and a link to buyers' messages.
 */
export default function MyClusterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [clusters, setClusters] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    produceAPI.myClusters().then((res) => setClusters(res.data.clusters)).catch(() => setClusters([]));
    produceAPI.unread().then((res) => setUnread(res.data.unread)).catch(() => {});
  }, []);

  return (
    <div className="p-4 md:p-6 animate-fadeIn pb-8 space-y-4" data-testid="my-cluster">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t('produceMarket.cluster.title')}</h1>
        <p className="text-sm text-gray-600">{t('produceMarket.cluster.subtitle')}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => navigate('/produce/chats')}
                className="flex items-center justify-center gap-2 rounded-2xl bg-farm-primary text-white font-semibold h-14"
                data-testid="cluster-messages">
          <MessageCircle className="w-5 h-5" /> {t('produceMarket.chats.title')}
          {unread > 0 && <span className="rounded-full bg-red-600 text-white text-xs px-2">{unread}</span>}
        </button>
        <button type="button" onClick={() => navigate('/produce')}
                className="flex items-center justify-center gap-2 rounded-2xl bg-white text-farm-forest font-semibold h-14 shadow-sm">
          <Store className="w-5 h-5" /> {t('nav.produce')}
        </button>
      </div>
      {clusters === null && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>}
      {clusters?.length === 0 && <p className="text-sm text-gray-600">{t('produceMarket.cluster.notManager')}</p>}
      {(clusters || []).map((c) => <ClusterSection key={c.cluster.id} data={c} />)}
    </div>
  );
}

function ClusterSection({ data }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { cluster, totals, listings } = data;
  return (
    <section data-testid="cluster-section">
      <div className="rounded-2xl bg-white shadow-sm p-4 mb-3">
        <p className="text-sm text-gray-600 flex items-center gap-1"><Users className="w-4 h-4" />
          {t(`produceMarket.cluster.kind.${cluster.kind}`)}</p>
        <h2 className="font-heading text-xl font-bold text-farm-forest break-words" data-testid="cluster-name">{cluster.name}</h2>
        <p className="text-sm text-gray-600 mt-1">{t('produceMarket.cluster.share', { name: cluster.name })}</p>
      </div>
      <HomeSectionTitle>{t('produceMarket.cluster.totals')}</HomeSectionTitle>
      <div className="grid grid-cols-2 gap-2 mb-4" data-testid="cluster-totals">
        {totals.length === 0 && <p className="col-span-2 text-sm text-gray-600">{t('produceMarket.cluster.none')}</p>}
        {totals.map((tot) => (
          <div key={`${tot.item}-${tot.unit}`} className="rounded-2xl bg-white shadow-sm p-3">
            <p className="font-semibold break-words">{tot.item}</p>
            <p className="text-xl font-extrabold text-farm-primary">{fmtQty(tot.quantity, lang)} {unitLabel(t, tot.unit)}</p>
            <p className="text-xs text-gray-500">{t('produceMarket.market.fromFarmers', { n: tot.farmers })}</p>
          </div>
        ))}
      </div>
      {listings.length > 0 && <HomeSectionTitle>{t('produceMarket.cluster.listings')}</HomeSectionTitle>}
      <ul className="space-y-2" data-testid="cluster-listings">
        {listings.map((l) => (
          <li key={l.id} className="rounded-2xl bg-white shadow-sm p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold break-words">{l.item}: {fmtQty(l.quantity, lang)} {unitLabel(t, l.unit)}</p>
              <p className="text-sm text-gray-600 break-words">{l.farmer_name}{l.location ? ` · ${l.location}` : ''}</p>
              <p className="text-xs text-gray-500">{t('produceMarket.market.bestBefore', { date: fmtDate(l.best_before, lang) })}
                {' · '}{t('produceMarket.market.updated', { date: fmtDate(l.updated_at, lang) })}</p>
            </div>
            {l.farmer_phone && (
              <a href={`tel:${l.farmer_phone}`} className="shrink-0 rounded-full bg-farm-primary/10 p-3" aria-label={t('produceMarket.cluster.call')}>
                <Phone className="w-5 h-5 text-farm-primary" />
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
